import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api, { errMsg } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageToggle } from "@/components/SiteNav";
import { toast } from "sonner";

export default function Login() {
  const { t } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success(t("auth.resetSent"));
        setMode("login");
        return;
      }
      const user = mode === "login"
        ? await login(form.email, form.password, remember)
        : await register(form.name, form.email, form.password, remember);
      if (user.role === "admin") navigate("/admin");
      else navigate(params.get("next") === "book" ? "/dashboard?book=1" : "/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      <div className="flex items-center justify-between px-4 sm:px-6 h-16 border-b border-border">
        <Link to="/" onClick={(e) => { e.preventDefault(); navigate("/"); window.scrollTo({ top: 0 }); }} className="flex items-center" data-testid="login-logo">
          <img src="/logo.png" alt="English Universe Stars" className="h-14 w-auto" />
        </Link>
        <LanguageToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-border shadow-lg">
          <CardContent className="p-8">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold" data-testid="auth-title">
              {mode === "forgot" ? t("auth.forgotTitle") : mode === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {mode === "forgot" ? t("auth.forgotSub") : mode === "login" ? t("auth.loginSub") : t("auth.registerSub")}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="auth-name">{t("auth.name")}</Label>
                  <Input id="auth-name" name="name" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="auth-email">{t("auth.email")}</Label>
                <Input id="auth-email" name="email" type="email" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
              </div>
              {mode !== "forgot" && (<>
              <div className="space-y-1.5">
                <Label htmlFor="auth-password">{t("auth.password")}</Label>
                <Input id="auth-password" name="password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none" htmlFor="auth-remember">
                <input id="auth-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="auth-remember-checkbox" />
                {t("auth.rememberMe")}
              </label>
              </>)}
              <Button type="submit" className="w-full" size="lg" disabled={busy} data-testid="auth-submit-btn">
                {busy ? (mode === "login" ? t("auth.loggingIn") : t("auth.sending")) : (mode === "forgot" ? t("auth.sendReset") : mode === "login" ? t("auth.login") : t("auth.register"))}
              </Button>
            </form>
            {mode === "login" && (
              <button
                className="mt-3 block text-sm text-muted-foreground hover:underline"
                onClick={() => setMode("forgot")}
                data-testid="auth-forgot-link"
              >
                {t("auth.forgot")}
              </button>
            )}
            <button
              className="mt-4 text-sm text-primary font-semibold hover:underline"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-mode-toggle"
            >
              {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
